import AttachFile from "@mui/icons-material/AttachFile";
import AutoAwesome from "@mui/icons-material/AutoAwesome";
import Delete from "@mui/icons-material/Delete";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import Download from "@mui/icons-material/Download";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import RateReviewOutlined from "@mui/icons-material/RateReviewOutlined";
import Refresh from "@mui/icons-material/Refresh";
import UploadFile from "@mui/icons-material/UploadFile";
import Visibility from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { isPreviewable } from "components/Document/shared/fileTypeUtils";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import {
  DocumentType,
  ExtractionStatus,
  ProcessedManifestDocumentsQuery,
  WasteManifestIngestSource,
  WasteManifestSource,
} from "generated-graphql/graphql";

import { SourceLetterBox } from "../SourceLetterBox";
import { SectionHeader } from "./SectionHeader";
import { ManifestPdfDocument } from "./types";

/** Synthetic grid row id for the "no manifest document" empty-state row. */
const MISSING_MANIFEST_ROW_ID = "__missing-manifest-document__";

/** Document types that represent a manifest PDF (processable into manifest data). */
const MANIFEST_DOCUMENT_TYPES = new Set<DocumentType>([
  DocumentType.RcraManifest,
  DocumentType.RcraManifestPrintedCopy,
]);

/** S3 key prefix the EPA-scan ingest + backfill always write for printed copies. */
const EPA_SCAN_STORAGE_PREFIX = "epa-manifest-printed-copies/";

/**
 * Manifest-PDF row label, derived from the document's type rather than its slot
 * — so a doc that became the data source via a switch (e.g. an RCRAInfo printed
 * copy) is labeled correctly instead of always reading "User Uploaded".
 */
function manifestPdfLabel(documentType: DocumentType | null | undefined): string {
  return documentType === DocumentType.RcraManifestPrintedCopy
    ? "Manifest (RCRAInfo)"
    : "Manifest (User Uploaded)";
}

/**
 * Label for a PRIMARY manifest-PDF row. An EPA-scan source must keep its
 * RCRAInfo identity even after Process re-types the underlying Document
 * (RCRAManifestPrintedCopy -> RCRAManifest) — at which point documentType alone
 * would mislabel it "User Uploaded". The pillar's ingestSource is the canonical
 * identity, but it isn't carried on the primary Document shape (and
 * documentManifest.ingestSource is review-gated), so we key on the stable
 * storageLink prefix that the EPA-scan ingest/backfill always write. Additional
 * (sibling) rows already label off ingestSource directly.
 */
function manifestPdfLabelForPrimary(
  documentType: DocumentType | null | undefined,
  storageLink: string | null | undefined
): string {
  if (storageLink?.startsWith(EPA_SCAN_STORAGE_PREFIX)) {
    return manifestPdfLabel(DocumentType.RcraManifestPrintedCopy);
  }
  return manifestPdfLabel(documentType);
}

/**
 * Manifest-PDF row label for additional (non-primary) document pillars, derived
 * from the WasteManifest ingest source. EPA-scan attachments report as RCRAInfo;
 * everything else defaults to User Uploaded.
 */
function manifestPdfLabelFromIngestSource(
  ingestSource: WasteManifestIngestSource | null | undefined
): string {
  return ingestSource === WasteManifestIngestSource.RcraInfoAttachment
    ? "Manifest (RCRAInfo)"
    : "Manifest (User Uploaded)";
}

type SupportingDoc = NonNullable<
  ProcessedManifestDocumentsQuery["processedManifests"]["items"][number]["documents"]
>[number];

type ExceptionReportDoc = NonNullable<
  NonNullable<
    ProcessedManifestDocumentsQuery["processedManifests"]["items"][number]["exceptionReportSubmission"]
  >["documents"]
>[number];

type AdditionalManifestDoc =
  ProcessedManifestDocumentsQuery["processedManifests"]["items"][number]["additionalManifestDocuments"][number];

interface DocumentsSectionProps {
  uploadedPdf: ManifestPdfDocument | null | undefined;
  rcraInfoApiPdf?: ManifestPdfDocument | null;
  /** Secondary manifest document pillars (e.g. the EPA scan alongside a human upload). */
  additionalManifestDocuments?: AdditionalManifestDoc[];
  supportingDocuments: SupportingDoc[] | undefined;
  exceptionReportDocuments: ExceptionReportDoc[] | undefined;
  documentsError: boolean;
  loading?: boolean;
  showExceptionReporting: boolean;
  onAttach: () => void;
  onPreviewSupporting: (doc: { id: string; title: string; fileExtension?: string | null }) => void;
  onDownload: (documentId: string) => void;
  onDetach: (manifestDocumentId: string) => void;
  onViewPdf: (document: ManifestPdfDocument) => void;
  /**
   * Document id currently open in the preview rail; its row gets a light-blue
   * highlight (matching the discrepancy dialog's previewed-row accent).
   */
  previewingDocumentId?: string | null;
  /**
   * Document id the manifest data (document pillar) was extracted from. That row
   * gets the source-document dot and the review/edit-manifest-data action.
   */
  sourceDocumentId?: string | null;
  /** True once manifest data has been extracted (labels the action Edit vs View). */
  manifestDataExtracted?: boolean;
  /** The source document is flagged needs-review (warns the action). */
  manifestDocumentNeedsReview?: boolean;
  /**
   * The source document's extracted data exists but is pending Encamp review
   * (same state as the Data Sources "pending review" marker). Shows a
   * tooltip-only indicator on the source-document row for users without the
   * staff review affordance, so the pending state is visible here too.
   */
  documentPendingReview?: boolean;
  /** Whether a manifest source document exists; drives the missing-document row. */
  hasManifestDocument?: boolean;
  /** Staff + flag gate for the manifest-document affordances. */
  canManageManifestDocument?: boolean;
  /**
   * Whether the detach action is offered. Staff-only for now (the detach
   * mutation also allows facility-permissioned customers, but we keep the
   * affordance staff-only until that flow is product-ready). Attach, view, and
   * download remain available to all users.
   */
  canDetach?: boolean;
  /** Open the review/edit dialog for the given manifest document (source or sibling). */
  onReviewManifestDocument?: (documentId: string) => void;
  /** Open the upload dialog from the missing-document row. */
  onUploadManifestDocument?: () => void;
  /** Process a non-source manifest document into the manifest data (switch source). */
  onProcessManifestDocument?: (
    documentId: string,
    documentName: string,
    fileExtension?: string | null
  ) => void;
  /**
   * True when the source document has been updated to a new PDF version since
   * the last review (documentStale). Drives the staff Refresh action.
   */
  documentStale?: boolean;
  /**
   * Staff action: re-process the source document from its new version (Refresh)
   * — runs the process + review flow on the stale source doc.
   */
  onRefreshManifestDocument?: () => void;
}

interface DocumentRow {
  id: string;
  /** The underlying Document id (distinct from the grid row id for supporting docs). */
  documentId: string;
  documentType?: DocumentType | null;
  typeLabel: string;
  name: string;
  fileExtension?: string | null;
  /** Open in the preview rail (manifest PDFs) or full-screen (supporting docs). */
  onView?: () => void;
  onDownload?: () => void;
  /** Detach is only offered for manifest-attached docs, not submission docs. */
  onDetach?: () => void;
  /** Marks the synthetic "no manifest document" empty-state row. */
  missing?: boolean;
  /**
   * The pillar's ingest source (manifest-PDF rows only). Used by the Process
   * gate: a non-RCRAInfo sibling can't outrank a reviewed RCRAInfo primary.
   */
  ingestSource?: WasteManifestIngestSource | null;
  /**
   * The document's own extraction status (manifest-PDF rows only). Drives two
   * things: (1) in-flight handling — PENDING shows a "Processing…" indicator and
   * PENDING/SUCCEEDED both suppress the "Process" action (mid-pipeline, or done
   * extracting and awaiting promotion); (2) the Process gate — an already-
   * extracted (SUCCEEDED) sibling has nothing left to process. Undefined on rows
   * it doesn't apply to (e.g. the RCRAInfo row stays processable).
   */
  extractionStatus?: ExtractionStatus | null;
}

/**
 * Manifest documents as a single grid that grows as documents are added — one
 * row per manifest PDF (origin-labeled) and per supporting document (Proof of
 * Contact, Exception Report, Other). Source-agnostic.
 */
export function DocumentsSection({
  uploadedPdf,
  rcraInfoApiPdf,
  additionalManifestDocuments,
  supportingDocuments,
  exceptionReportDocuments,
  documentsError,
  loading,
  showExceptionReporting,
  onAttach,
  onPreviewSupporting,
  onDownload,
  onDetach,
  onViewPdf,
  previewingDocumentId,
  sourceDocumentId,
  manifestDataExtracted,
  manifestDocumentNeedsReview,
  hasManifestDocument,
  canManageManifestDocument,
  canDetach,
  documentPendingReview,
  onReviewManifestDocument,
  onUploadManifestDocument,
  onProcessManifestDocument,
  documentStale,
  onRefreshManifestDocument,
}: DocumentsSectionProps) {
  const docs = supportingDocuments ?? [];

  // A reviewed RCRAInfo source is the legal source of truth — process-manifests
  // ranks reviewed-first, then RCRAInfoAttachment over DocumentUpload — so no
  // non-RCRAInfo sibling can ever outrank it. Drives the Process-action guard
  // that stops offering a no-op "switch source" on losing siblings. (Primary
  // source is the uploadedPdf/manifestPdf; its EPA-scan storageLink prefix is
  // the stable RCRAInfo signal, since documentType is mutated by extraction.)
  const primaryIsReviewedRcraInfo =
    sourceDocumentId != null &&
    manifestDataExtracted === true &&
    manifestDocumentNeedsReview !== true &&
    (uploadedPdf?.storageLink?.startsWith(EPA_SCAN_STORAGE_PREFIX) ?? false);

  const manifestPdfRows: DocumentRow[] = [
    ...(uploadedPdf
      ? [
          {
            id: uploadedPdf.id,
            documentId: uploadedPdf.id,
            documentType: uploadedPdf.documentType,
            typeLabel: manifestPdfLabelForPrimary(
              uploadedPdf.documentType,
              uploadedPdf.storageLink
            ),
            name: uploadedPdf.title,
            fileExtension: uploadedPdf.fileExtension,
            ingestSource: uploadedPdf.storageLink?.startsWith(EPA_SCAN_STORAGE_PREFIX)
              ? WasteManifestIngestSource.RcraInfoAttachment
              : WasteManifestIngestSource.DocumentUpload,
            extractionStatus: uploadedPdf.extractionStatus,
            onView: () => onViewPdf(uploadedPdf),
            onDownload: () => onDownload(uploadedPdf.id),
          },
        ]
      : []),
    // Skip the RCRAInfo row when it's the same Document as the uploaded/source
    // PDF (e.g. after switching the data source to the printed copy) — two grid
    // rows with the same id would make MUI DataGrid reject the whole set.
    ...(rcraInfoApiPdf && rcraInfoApiPdf.id !== uploadedPdf?.id
      ? [
          {
            id: rcraInfoApiPdf.id,
            documentId: rcraInfoApiPdf.id,
            documentType: rcraInfoApiPdf.documentType,
            typeLabel: manifestPdfLabelForPrimary(
              rcraInfoApiPdf.documentType,
              rcraInfoApiPdf.storageLink
            ),
            name: rcraInfoApiPdf.title,
            fileExtension: rcraInfoApiPdf.fileExtension,
            // The RCRAInfo API printed copy is, by definition, an RCRAInfo source.
            ingestSource: WasteManifestIngestSource.RcraInfoAttachment,
            extractionStatus: rcraInfoApiPdf.extractionStatus,
            onView: () => onViewPdf(rcraInfoApiPdf),
            onDownload: () => onDownload(rcraInfoApiPdf.id),
          },
        ]
      : []),
    // Additional sibling manifest documents (e.g. an EPA scan ingested alongside
    // a human-uploaded copy). Each has its own WasteManifest row (distinct
    // wasteManifestId) — use that as the grid row id so it never collides with
    // the sourceDocument row (which uses the Document id).
    ...(additionalManifestDocuments ?? []).flatMap((amd) => {
      const doc = amd.sourceDocument;
      if (!doc) return [];
      // Deduplicate: skip if this Document is already shown as the primary row.
      if (doc.id === uploadedPdf?.id) return [];
      if (doc.id === rcraInfoApiPdf?.id) return [];
      return [
        {
          id: amd.wasteManifestId,
          documentId: doc.id,
          documentType: doc.documentType,
          typeLabel: manifestPdfLabelFromIngestSource(amd.ingestSource),
          name: doc.title,
          fileExtension: doc.fileExtension,
          ingestSource: amd.ingestSource,
          extractionStatus: doc.extractionStatus,
          onView: () =>
            onViewPdf({
              id: doc.id,
              title: doc.title,
              fileExtension: doc.fileExtension,
              storageLink: doc.storageLink,
              documentType: doc.documentType,
              sizeMegabytes: doc.sizeMegabytes,
              createdAt: doc.createdAt,
              extractionStatus: doc.extractionStatus,
            }),
          onDownload: () => onDownload(doc.id),
        } satisfies DocumentRow,
      ];
    }),
  ];

  // Manifest-type docs attached to this manifest that aren't (yet) the data
  // source — e.g. a freshly-uploaded manifest pre-associated up front while the
  // async pipeline processes it into the data source. The "Other" bucket below
  // deliberately excludes manifest types, so without this they'd be invisible
  // until promoted. Deduped against the source PDF and the RCRAInfo row (both
  // rendered above) so no row id appears twice.
  const attachedManifestCopyRows: DocumentRow[] = docs
    .filter(
      (d) =>
        MANIFEST_DOCUMENT_TYPES.has(d.document.documentType) &&
        d.document.id !== sourceDocumentId &&
        d.document.id !== rcraInfoApiPdf?.id
    )
    .map((d) => ({
      id: d.id,
      documentId: d.document.id,
      documentType: d.document.documentType,
      typeLabel: manifestPdfLabel(d.document.documentType),
      name: d.document.title,
      fileExtension: d.document.fileExtension,
      onView: () => onViewPdf(d.document),
      onDownload: () => onDownload(d.document.id),
      onDetach: () => onDetach(d.id),
      // Drives in-flight handling: while it's being extracted/incorporated as the
      // data source, don't offer "Process".
      extractionStatus: d.document.extractionStatus,
    }));

  const supportingRow = (d: SupportingDoc, typeLabel: string): DocumentRow => ({
    id: d.id,
    documentId: d.document.id,
    documentType: d.document.documentType,
    typeLabel,
    name: d.document.title,
    fileExtension: d.document.fileExtension,
    onView: isPreviewable(d.document.fileExtension)
      ? () => onPreviewSupporting(d.document)
      : undefined,
    onDownload: () => onDownload(d.document.id),
    onDetach: () => onDetach(d.id),
  });

  const supportingRows: DocumentRow[] = [
    ...docs
      .filter((d) => d.document.documentType === DocumentType.WasteTransporterContactProof)
      .map((d) => supportingRow(d, "45-Day Proof of Contact")),
    ...(showExceptionReporting
      ? docs
          .filter((d) => d.document.documentType === DocumentType.WasteExceptionReportProof)
          .map((d) => supportingRow(d, "60-Day Exception Report"))
      : []),
    ...(showExceptionReporting
      ? (exceptionReportDocuments ?? []).map((d) => ({
          id: d.id,
          documentId: d.document.id,
          documentType: d.document.documentType,
          typeLabel: "60-Day Exception Report",
          name: d.document.title,
          fileExtension: d.document.fileExtension,
          onView: isPreviewable(d.document.fileExtension)
            ? () => onPreviewSupporting(d.document)
            : undefined,
          onDownload: () => onDownload(d.document.id),
        }))
      : []),
    ...docs
      .filter(
        (d) =>
          d.document.documentType !== DocumentType.WasteTransporterContactProof &&
          d.document.documentType !== DocumentType.WasteExceptionReportProof &&
          // Manifest documents (uploaded or the RCRAInfo printed copy) are
          // surfaced as their own "Manifest" row above — never as "Other".
          !MANIFEST_DOCUMENT_TYPES.has(d.document.documentType)
      )
      .map((d) => supportingRow(d, "Other")),
  ];

  // Empty-state row: when there's no manifest document, surface a full-width
  // "no manifest document" row with an Upload action (in place of the old
  // header button) rather than silently omitting it. Suppressed once a manifest
  // doc is attached but not yet the data source (it shows as its own row above),
  // so we don't contradict it with a "no manifest document" message.
  const showMissingRow =
    !!canManageManifestDocument && !hasManifestDocument && attachedManifestCopyRows.length === 0;
  const missingRow: DocumentRow = {
    id: MISSING_MANIFEST_ROW_ID,
    documentId: MISSING_MANIFEST_ROW_ID,
    typeLabel: "",
    name: "",
    missing: true,
  };

  // Always sits as the last row, after any real documents.
  const rows = [
    ...manifestPdfRows,
    ...attachedManifestCopyRows,
    ...supportingRows,
    ...(showMissingRow ? [missingRow] : []),
  ];

  // A source document with needsReview=false but reviewedAt unset is still
  // pending review (excluded from availableSources) — documentPendingReview
  // carries that state, so treat it as needing review in both the per-row icon
  // label and the header CTA.
  const reviewNeeded = manifestDocumentNeedsReview || !!documentPendingReview;
  const reviewActionLabel = reviewNeeded
    ? "Review manifest document data"
    : manifestDataExtracted
      ? "Edit manifest document data"
      : "View document";

  // Narrow nullable sourceDocumentId to a const so TS can track it through the
  // onClick closure without a non-null assertion.
  const reviewSourceId = sourceDocumentId ?? null;
  const reviewHeaderLabel = reviewNeeded ? "Review Document Data" : "Edit Document Data";

  const columns: CustomGridColDef<DocumentRow>[] = [
    {
      field: "typeLabel",
      headerName: "Type",
      width: 200,
      resizable: false,
      // The missing-document row spans the full grid: message on the left,
      // Upload action on the right.
      colSpan: (_, row) => (row.missing ? 4 : undefined),
      renderCell: ({ row }) => {
        if (row.missing) {
          const missingText = rcraInfoApiPdf
            ? "No processed manifest document — upload a manifest or process the manifest from RCRAInfo"
            : "No manifest document uploaded";
          return (
            <Stack direction="row" spacing={1} alignItems="center" width="100%" height="100%">
              {/* Text flexes + ellipsizes; the icon and button never shrink, so the
                  button stays fully visible as the drawer/rail narrows the cell. */}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                <DescriptionOutlined
                  fontSize="small"
                  sx={{ color: "text.disabled", flexShrink: 0 }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  noWrap
                  title={missingText}
                  // pr gives the italic slant room so the trailing glyph isn't
                  // clipped by the noWrap overflow:hidden when the text fits.
                  sx={{ fontStyle: "italic", minWidth: 0, pr: "2px" }}
                >
                  {rcraInfoApiPdf ? (
                    <>
                      No{" "}
                      <Box component="span" sx={{ fontWeight: 700 }}>
                        processed
                      </Box>{" "}
                      manifest document — upload a manifest or process the manifest from RCRAInfo
                    </>
                  ) : (
                    "No manifest document uploaded"
                  )}
                </Typography>
              </Stack>
              <Button
                size="small"
                color="staff"
                startIcon={<UploadFile fontSize="small" />}
                onClick={onUploadManifestDocument}
                sx={{ flexShrink: 0 }}
              >
                Upload manifest document
              </Button>
            </Stack>
          );
        }
        const isSource = sourceDocumentId != null && row.documentId === sourceDocumentId;
        return (
          <Stack direction="row" spacing={0.75} alignItems="center">
            {/* Fixed-width slot (sized to the letter box) keeps every Type label
                aligned; the "D" marker shows only on the row whose document is the
                CONTRIBUTING data source. An artifact-only / unreviewed source is
                linked via documentManifestId but its data isn't live yet, so we
                gate on !documentPendingReview — matching the greyed-out Document
                pill in DATA SOURCES (same availableSources signal). Without this
                an unprocessed scan would wrongly read as the active data source. */}
            <Box sx={{ width: 15, flexShrink: 0, display: "flex" }}>
              {isSource && !documentPendingReview && (
                <SourceLetterBox
                  source={WasteManifestSource.ManifestDocument}
                  tooltip="Data source document"
                />
              )}
            </Box>
            <Typography variant="body2">{row.typeLabel}</Typography>
          </Stack>
        );
      },
    },
    {
      // The only flexing column — it absorbs all width changes as the drawer /
      // rail resize; the others are pinned.
      field: "name",
      headerName: "Document",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "fileExtension",
      headerName: "Format",
      width: 90,
      resizable: false,
      renderCell: ({ row }) =>
        row.fileExtension ? (
          <Chip
            label={row.fileExtension.toUpperCase()}
            size="small"
            variant="outlined"
            sx={{ my: "auto" }}
          />
        ) : (
          "—"
        ),
    },
    {
      field: "actions",
      headerName: "",
      // Pinned to fit up to four action icons — Preview, Download, Review/Edit
      // (or Process / Refresh / progress spinner), and Detach — so the last one
      // isn't clipped. Refresh and the spinner never co-occur with Detach on the
      // same row (stale source vs. detachable sibling), so four is the max.
      width: 164,
      minWidth: 164,
      maxWidth: 164,
      sortable: false,
      resizable: false,
      renderCell: ({ row }) => {
        // The missing row spans all columns, so its Actions cell isn't rendered.
        if (row.missing) return null;
        const isSource = sourceDocumentId != null && row.documentId === sourceDocumentId;
        const isManifestDocument =
          row.documentType != null && MANIFEST_DOCUMENT_TYPES.has(row.documentType);
        // A manifest PDF can be processed (run through doc intelligence) to
        // become / switch the data source when:
        //  - it is not the current source, OR
        //  - it IS the source but extraction hasn't run yet (artifact-only
        //    primary: the scan was ingested but no data was extracted from it), OR
        //  - it IS the stale source (documentStale allows Refresh even when
        //    already extracted, since canProcess is normally false there).
        // Guards that keep Process from showing where it would be a no-op:
        //  A. the sibling's own extraction already SUCCEEDED — nothing to process.
        //  B. a non-RCRAInfo sibling can't outrank a reviewed RCRAInfo primary
        //     (process-manifests ranks reviewed-first, then RCRAInfoAttachment),
        //     so processing it would never make it the source.
        //  C. extraction is mid-pipeline (PENDING) or done and awaiting promotion
        //     (SUCCEEDED) — the doc is already being incorporated.
        const siblingAlreadyProcessed =
          !isSource && row.extractionStatus === ExtractionStatus.Succeeded;
        const siblingCannotOutrankPrimary =
          !isSource &&
          primaryIsReviewedRcraInfo &&
          row.ingestSource !== WasteManifestIngestSource.RcraInfoAttachment;
        const extractionInFlight =
          row.extractionStatus === ExtractionStatus.Pending ||
          row.extractionStatus === ExtractionStatus.Succeeded;
        const canProcess =
          !!canManageManifestDocument &&
          !!onProcessManifestDocument &&
          isManifestDocument &&
          (!isSource || manifestDataExtracted === false || (!!documentStale && isSource)) &&
          !siblingAlreadyProcessed &&
          !siblingCannotOutrankPrimary &&
          !extractionInFlight;
        // Refresh is the stale-source action (staff): re-process the new version.
        // The "New version available" badge + the separate Accept action were
        // removed — Refresh's tooltip conveys the new version.
        const showRefresh = !!canManageManifestDocument && isSource && !!documentStale;
        // Review/Edit appears wherever a manifest doc has extracted data to edit:
        // the source (review-gated label) and any already-processed sibling. On a
        // stale source it shows ALONGSIDE Refresh — view/edit the current data, or
        // Refresh to re-process the new version. Process stays mutually exclusive
        // with Edit (unprocessed → Process, processed → Edit).
        const isProcessedSibling = !isSource && row.extractionStatus === ExtractionStatus.Succeeded;
        const showReviewEdit =
          !!canManageManifestDocument &&
          !!onReviewManifestDocument &&
          isManifestDocument &&
          ((isSource && (manifestDocumentNeedsReview || manifestDataExtracted)) ||
            isProcessedSibling);
        const rowReviewLabel = isSource ? reviewActionLabel : "Edit manifest document data";
        // A manifest document whose extraction failed. Surface it explicitly
        // (there's no other in-drawer signal) with an instruction keyed to what
        // the viewer can actually do: staff re-run Process (the sparkle beside
        // this icon), customers reach out. Matches the "Failed" wording of the
        // Document Review column (documentStateDisplay.ts).
        const extractionFailed =
          isManifestDocument && row.extractionStatus === ExtractionStatus.Failed;
        const failedInstruction = canManageManifestDocument
          ? "Data extraction failed. Re-process to try again, or upload a better copy."
          : "Data extraction failed. Reach out to your Encamp contact.";
        return (
          // Stop row-click (preview toggle) from firing when an action is clicked.
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            height="100%"
            onClick={(e) => e.stopPropagation()}
          >
            {row.onView && (
              <IconButton size="small" onClick={row.onView} title="Preview">
                <Visibility fontSize="small" />
              </IconButton>
            )}
            {row.onDownload && (
              <IconButton size="small" onClick={row.onDownload} title="Download">
                <Download fontSize="small" />
              </IconButton>
            )}
            {/* Review/Edit. On an artifact-only source the label would degrade to
                "View document" (redundant with the eye), so showReviewEdit gates
                it on there being extracted data to edit. Opens the review/edit
                dialog for this row's own document. */}
            {showReviewEdit && (
              <Tooltip title={rowReviewLabel}>
                <IconButton
                  size="small"
                  onClick={() => onReviewManifestDocument?.(row.documentId)}
                  color="staff"
                  aria-label={rowReviewLabel}
                >
                  <DescriptionOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {/* Tooltip-only counterpart for users without the staff review
                action: same icon + wording as the Data Sources marker, so the
                pending-review state is visible on the document row too. */}
            {isSource && !canManageManifestDocument && documentPendingReview && (
              <Tooltip title="Manifest document data is pending review by Encamp staff">
                <DescriptionOutlined
                  fontSize="small"
                  color="warning"
                  aria-label="Manifest document data is pending review by Encamp staff"
                />
              </Tooltip>
            )}
            {/* Failed extraction: an explicit, always-visible signal + what to
                do about it. Sits just before Process so, for staff, the failure
                and its re-process action read together. */}
            {extractionFailed && (
              <Tooltip title={failedInstruction}>
                <ErrorOutline fontSize="small" color="error" aria-label={failedInstruction} />
              </Tooltip>
            )}
            {canProcess && !showRefresh && (
              <Tooltip title="Process manifest document data">
                <IconButton
                  size="small"
                  color="staff"
                  onClick={() =>
                    onProcessManifestDocument?.(row.documentId, row.name, row.fileExtension)
                  }
                  aria-label="Process manifest document data"
                >
                  <AutoAwesome fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {/* Stale-source action: a newer version of the source document was
                ingested since review. Refresh re-runs the process + review flow on
                it. Sole stale affordance — the tooltip conveys the new version. */}
            {showRefresh && (
              <Tooltip title="New version available — refresh to re-process">
                <IconButton
                  size="small"
                  color="staff"
                  onClick={() => onRefreshManifestDocument?.()}
                  aria-label="New version available — refresh to re-process"
                >
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {/* Actively extracting: show progress instead of a Process action so
                it's clear the document is already being processed. (A SUCCEEDED
                copy awaiting promotion also suppresses Process but shows no
                spinner — it would otherwise hang if promotion never lands.) */}
            {row.extractionStatus === ExtractionStatus.Pending && !isSource && (
              <Tooltip title="Processing manifest document data…">
                <CircularProgress
                  size={16}
                  color="warning"
                  aria-label="Processing manifest document data"
                />
              </Tooltip>
            )}
            {row.onDetach && canDetach && (
              <IconButton size="small" onClick={row.onDetach} title="Detach">
                <Delete fontSize="small" />
              </IconButton>
            )}
          </Stack>
        );
      },
    },
  ];

  return (
    <Box>
      <SectionHeader
        title="Documents"
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            {!!canManageManifestDocument &&
              !!onReviewManifestDocument &&
              reviewSourceId != null &&
              (manifestDocumentNeedsReview || manifestDataExtracted) && (
                <Button
                  size="small"
                  color="staff"
                  startIcon={<RateReviewOutlined fontSize="small" />}
                  onClick={() => onReviewManifestDocument?.(reviewSourceId)}
                >
                  {reviewHeaderLabel}
                </Button>
              )}
            <Button startIcon={<AttachFile />} size="small" onClick={onAttach}>
              Attach Document
            </Button>
          </Stack>
        }
      >
        <InfoIconTooltip
          tooltip={
            "Only one document can be the active manifest document data source at a time. " +
            "Additional manifest copies and other associated documents (proof of vendor contact, " +
            "exception reports, etc.) can still be uploaded and attached."
          }
          fontSize="1rem"
          iconButtonSx={{ p: 0 }}
        />
      </SectionHeader>

      {documentsError && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          Failed to load documents
        </Typography>
      )}

      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        loading={loading}
        disableRowSelectionOnClick
        hideFooter
        autoHeight
        localeText={{ noRowsLabel: "No documents yet" }}
        // Click a row to preview it (manifest PDFs toggle the side rail open/
        // closed; supporting docs open full-screen). Action buttons stop
        // propagation so they don't double-trigger.
        onRowClick={({ row }) => row.onView?.()}
        // Highlight the row whose document is open in the preview rail (matches
        // the discrepancy dialog's previewed-row accent).
        getRowClassName={({ row }) =>
          previewingDocumentId != null && row.documentId === previewingDocumentId
            ? "row--previewing"
            : ""
        }
        sx={{
          "& .MuiDataGrid-row": { cursor: "pointer" },
          "& .MuiDataGrid-row.row--previewing": {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
          },
          "& .MuiDataGrid-row.row--previewing:hover": {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
          },
        }}
      />
    </Box>
  );
}
